import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  PanelRightOpen, PanelRightClose, Send, Loader2, CheckCircle2,
  AlertTriangle, Cpu, Link2, Clock, Plus, FileText, Settings, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AgentPanel = ({ blueprintId, businessId, agentId }) => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('agent'); // agent | openclaw | activity
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [applying, setApplying] = useState(false);
  const [clawStatus, setClawStatus] = useState(null);
  const [activity, setActivity] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [blueprints, setBlueprints] = useState([]);
  const [selBiz, setSelBiz] = useState(businessId || '');
  const [selBp, setSelBp] = useState(blueprintId || '');
  const scrollRef = useRef(null);

  useEffect(() => { setSelBiz(businessId || ''); }, [businessId]);
  useEffect(() => { setSelBp(blueprintId || ''); }, [blueprintId]);

  useEffect(() => {
    if (open) {
      fetchContext();
      if (tab === 'openclaw') fetchClawStatus();
      if (tab === 'activity') fetchActivity();
    }
  }, [open, tab]);

  const fetchContext = async () => {
    try {
      const [bizRes, bpRes] = await Promise.all([
        axios.get(`${API}/businesses`),
        axios.get(`${API}/blueprints`),
      ]);
      setBusinesses(bizRes.data);
      setBlueprints(bpRes.data);
    } catch {}
  };

  const fetchClawStatus = async () => {
    try { const r = await axios.get(`${API}/agent-panel/integrations/openclaw/status`); setClawStatus(r.data); }
    catch { setClawStatus({ connected: false }); }
  };

  const fetchActivity = async () => {
    try {
      const [actRes, propRes] = await Promise.all([
        axios.get(`${API}/agent-panel/activity`),
        axios.get(`${API}/agent-panel/proposals`),
      ]);
      setActivity(actRes.data);
      setProposals(propRes.data);
    } catch {}
  };

  const handleRun = async (taskOverride) => {
    const task = taskOverride || input.trim();
    if (!task) return;
    setLoading(true);
    setProposal(null);
    try {
      const res = await axios.post(`${API}/agent-panel/tasks/run`, {
        task, provider: tab === 'openclaw' ? 'openclaw' : 'humanscaled',
        business_id: selBiz || undefined, blueprint_id: selBp || undefined,
      });
      setProposal(res.data);
      setInput('');
      toast.success('Proposal generated');
    } catch (err) { toast.error('Task failed'); }
    finally { setLoading(false); }
  };

  const handleApply = async (action) => {
    if (!proposal?.proposal_id) return;
    setApplying(true);
    try {
      const res = await axios.post(`${API}/agent-panel/proposals/apply`, { proposal_id: proposal.proposal_id, action });
      toast.success(action === 'create_draft' ? `Blueprint created: ${res.data.name}` : `Template saved: ${res.data.name}`);
      setProposal(null);
      fetchActivity();
    } catch (err) { toast.error(err.response?.data?.detail || 'Apply failed'); }
    finally { setApplying(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="fixed right-4 top-20 z-40 p-2 bg-[#0a0a0a] border border-white/10 text-muted-foreground hover:text-emerald-400 hover:border-emerald-500/30 transition-all" data-testid="agent-panel-toggle">
        <PanelRightOpen className="w-4 h-4" />
      </button>
    );
  }

  return (
    <div className="fixed right-0 top-0 bottom-0 z-50 w-80 bg-[#0a0a0a] border-l border-white/5 flex flex-col shadow-2xl" data-testid="agent-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-white/5">
        <div className="flex items-center gap-2">
          <Cpu className="w-4 h-4 text-emerald-400" />
          <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Agent Panel</span>
        </div>
        <button onClick={() => setOpen(false)} className="p-1 text-muted-foreground hover:text-white"><PanelRightClose className="w-4 h-4" /></button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-white/5">
        {[
          { key: 'agent', label: 'HumanScaled' },
          { key: 'openclaw', label: 'OpenClaw' },
          { key: 'activity', label: 'Activity' },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} className={`flex-1 py-2 text-[10px] font-mono uppercase ${tab === t.key ? 'text-emerald-400 border-b border-emerald-500' : 'text-muted-foreground hover:text-white'}`} data-testid={`panel-tab-${t.key}`}>{t.label}</button>
        ))}
      </div>

      {/* Context Selectors */}
      <div className="px-3 py-2 border-b border-white/5 space-y-1.5">
        <select value={selBiz} onChange={e => setSelBiz(e.target.value)} className="w-full bg-black/30 border border-white/10 rounded-sm p-1.5 text-[10px] text-white" data-testid="panel-biz-select">
          <option value="">No business selected</option>
          {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <select value={selBp} onChange={e => setSelBp(e.target.value)} className="w-full bg-black/30 border border-white/10 rounded-sm p-1.5 text-[10px] text-white" data-testid="panel-bp-select">
          <option value="">No blueprint selected</option>
          {blueprints.map(b => <option key={b.id} value={b.id}>{b.name} [v{b.version}]</option>)}
        </select>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* AGENT / OPENCLAW TAB */}
        {(tab === 'agent' || tab === 'openclaw') && (
          <div className="p-3 space-y-3">
            {/* OpenClaw status */}
            {tab === 'openclaw' && (
              <div className={`flex items-center gap-2 p-2 border text-[10px] ${clawStatus?.connected ? 'border-emerald-500/20 bg-emerald-500/5 text-emerald-400' : 'border-white/10 bg-white/5 text-muted-foreground'}`}>
                <div className={`w-2 h-2 rounded-full ${clawStatus?.connected ? 'bg-emerald-500' : 'bg-white/20'}`} />
                {clawStatus?.connected ? 'OpenClaw Connected' : 'Not configured'}
              </div>
            )}

            {/* Quick actions */}
            <div className="space-y-1">
              <p className="text-[10px] text-muted-foreground uppercase">Quick Actions</p>
              {[
                { label: 'Generate role templates', task: 'Generate role-specific workflow templates for common business roles' },
                { label: 'Draft workflow blueprint', task: 'Create a new workflow blueprint based on best practices' },
                { label: 'Propose improvements', task: 'Analyze the current blueprint and suggest improvements' },
              ].map((qa, i) => (
                <button key={i} onClick={() => handleRun(qa.task)} disabled={loading} className="w-full text-left px-2.5 py-2 bg-black/20 border border-white/5 hover:border-emerald-500/20 text-xs text-muted-foreground hover:text-white transition-colors" data-testid={`quick-action-${i}`}>
                  {qa.label}
                </button>
              ))}
            </div>

            {/* Custom task */}
            <div>
              <Textarea value={input} onChange={e => setInput(e.target.value)} placeholder="Describe a task..." className="bg-black/30 border-white/10 rounded-sm text-xs min-h-[50px]" data-testid="panel-task-input" />
              <Button onClick={() => handleRun()} disabled={loading || !input.trim()} className="w-full mt-1.5 bg-emerald-500 text-black hover:bg-emerald-400 rounded-sm h-8 text-xs" data-testid="panel-run-btn">
                {loading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />} Run Task
              </Button>
            </div>

            {/* Proposal Preview */}
            {proposal && (
              <div className="border border-emerald-500/20 bg-emerald-500/5 p-3 space-y-2" data-testid="panel-proposal">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-emerald-400 font-mono uppercase">{proposal.type || 'Proposal'}</span>
                  <button onClick={() => setProposal(null)} className="p-0.5 text-muted-foreground hover:text-white"><X className="w-3 h-3" /></button>
                </div>
                <p className="text-xs font-medium">{proposal.name}</p>
                <p className="text-[10px] text-muted-foreground">{proposal.summary}</p>
                {proposal.data && (
                  <div className="text-[10px] font-mono text-muted-foreground bg-black/30 p-2 max-h-24 overflow-auto">
                    {proposal.data.states && <p>{proposal.data.states.length} states</p>}
                    {proposal.data.triggers && <p>{proposal.data.triggers.length} triggers</p>}
                    {proposal.data.actions && <p>{proposal.data.actions.length} actions</p>}
                  </div>
                )}
                <div className="flex gap-1.5">
                  <Button onClick={() => handleApply('create_draft')} disabled={applying} size="sm" className="flex-1 bg-emerald-500 text-black hover:bg-emerald-400 rounded-sm h-7 text-[10px]" data-testid="panel-apply-btn">
                    {applying ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Plus className="w-3 h-3 mr-0.5" /> Apply as Draft</>}
                  </Button>
                  <Button onClick={() => handleApply('save_template')} disabled={applying} size="sm" variant="outline" className="border-white/10 rounded-sm h-7 text-[10px]" data-testid="panel-template-btn">
                    <FileText className="w-3 h-3 mr-0.5" /> Template
                  </Button>
                </div>
              </div>
            )}

            {proposal?.message && !proposal.proposal_id && (
              <div className="border border-white/10 bg-white/5 p-3"><p className="text-xs text-muted-foreground">{proposal.message}</p></div>
            )}
          </div>
        )}

        {/* ACTIVITY TAB */}
        {tab === 'activity' && (
          <div className="p-3 space-y-2">
            <p className="text-[10px] text-muted-foreground uppercase mb-2">Recent Proposals</p>
            {proposals.slice(0, 5).map(p => (
              <div key={p.id} className="flex items-start justify-between py-1.5 border-b border-white/5 text-[10px]">
                <div>
                  <span className={`font-mono px-1 py-0.5 mr-1 ${p.provider === 'openclaw' ? 'bg-violet-500/20 text-violet-400' : 'bg-emerald-500/20 text-emerald-400'}`}>{p.provider}</span>
                  <span className="text-muted-foreground">{p.name || p.task?.slice(0, 30)}</span>
                </div>
                <span className={`font-mono ${p.status === 'applied' ? 'text-emerald-400' : 'text-amber-400'}`}>{p.status}</span>
              </div>
            ))}
            <p className="text-[10px] text-muted-foreground uppercase mt-4 mb-2">Action Log</p>
            {activity.slice(0, 10).map(a => (
              <div key={a.id} className="flex items-center justify-between py-1 text-[10px] border-b border-white/5">
                <div className="flex items-center gap-1.5">
                  <span className="font-mono text-muted-foreground">{a.action_type}</span>
                  <span className="text-muted-foreground truncate max-w-[120px]">{a.payload_summary}</span>
                </div>
                <span className="text-muted-foreground">{new Date(a.created_at).toLocaleTimeString()}</span>
              </div>
            ))}
            {activity.length === 0 && proposals.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">No activity yet</p>}
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentPanel;
